<template>
  <BaseCard
    :with-style="false"
    title="Pengaturan Bank"
    description="Atur bank yang akan digunakan untuk pembayaran"
  >
    <Loading v-if="loading" />

    <template #action>
      <div class="flex items-center gap-2">
        <OfficeSelectSearch
          v-if="checkHasPermission('office_banks.update_global')"
          v-model="officeSearch"
          :shadow="false"
          :request-params="{
            'filter[is_active]': true,
            'filter[not-office_category_id]': '5',
          }"
          @change="loadOfficeBanks"
        />
        <BaseButton @click="onCreate"> Tambah Metode Pembayaran </BaseButton>
      </div>
    </template>
    <div class="border-t border-gray-200 pt-5">
      <div class="grid grid-cols-3 gap-8">
        <div
          v-for="bank in officeBanks.data"
          :key="bank.id"
          class="flex items-start space-x-4"
        >
          <BaseCheckbox
            :disabled="!checkQrisVerified(bank)"
            v-model="bank.attributes.is_active"
            :with-label="false"
            @change="(checked) => onChangeActive(bank, checked)"
          />
          <div class="mt-0.5 space-y-1">
            <div class="flex items-center space-x-4">
              <h4 class="text-sm font-medium text-gray-900">
                {{ bank.attributes.bank_name }}

                <span v-if="bank.attributes.office_bank_type === 'qris'"
                  >(QRIS)</span
                >
              </h4>
              <BaseBadge
                v-if="!checkQrisVerified(bank)"
                size="sm"
                color="yellow"
                >Belum Diverifikasi</BaseBadge
              >
              <div class="flex items-center gap-2">
                <button
                  v-on:click="onRequestVerify(bank)"
                  v-if="!checkQrisVerified(bank)"
                >
                  <Icon icon="heroicons:check" class="h-4 w-4 text-green-600" />
                </button>
                <button
                  v-on:click="onEdit(bank)"
                  v-if="
                    bank.attributes.office_bank_type === 'bank' ||
                    bank.attributes.office_bank_type === 'qris'
                  "
                >
                  <Icon
                    icon="heroicons:pencil-square"
                    class="h-4 w-4 text-green-600"
                  />
                </button>
                <button
                  v-on:click="onDelete(bank.id)"
                  v-if="
                    bank.attributes.office_bank_type === 'bank' ||
                    bank.attributes.office_bank_type === 'qris'
                  "
                >
                  <Icon icon="heroicons:trash" class="h-4 w-4 text-red-600" />
                </button>
              </div>
            </div>
            <p class="text-sm text-gray-500">
              a.n
              {{ bank.attributes.account_name }}
            </p>
            <p class="text-sm text-gray-500">
              No Rek:
              {{ bank.attributes.account_number ?? '-' }}
            </p>
            <template v-if="me.office_type === 'pusat'">
              <p class="text-sm text-gray-500">
                No GL:
                {{ bank.attributes.no_account_gl ?? '-' }}
              </p>
              <p class="text-sm text-gray-500">
                Cost Center:
                {{ bank.attributes.cost_center ?? '-' }}
              </p>
            </template>
          </div>
        </div>
      </div>
    </div>

    <BaseConfirm
      :visible="deleteConfirm.visible"
      icon-type="badge"
      title="Konfirmasi Penghapusan"
      confirm-text="Konfirmasi"
      @close="deleteConfirm.visible = false"
      @confirmed="onConfirmDelete"
    />

    <OfficeBankFormModal
      :visible="formModal.visible"
      :data="formModal.data"
      @close="formModal.visible = false"
      @saved="onSaved"
    />

    <BaseConfirm
      :visible="verifyConfirm.visible"
      icon-type="badge"
      type="success"
      title="Konfirmasi Edit QRIS"
      message="Klik konfirmasi untuk membuat pesan ke nomor whatsapp kami untuk memverifikasi edit QRIS."
      confirm-text="Buka Whatsapp"
      :with-cancel="false"
      @close="verifyConfirm.visible = false"
      @confirmed="onConfirmVerify"
    />
  </BaseCard>
</template>

<script>
import { mapGetters } from 'vuex';
import { requestMixin } from '../../mixins/request/request';
import BaseCard from '../base/BaseCard.vue';
import BaseCheckbox from '../base/BaseCheckbox.vue';
import BaseConfirm from '../base/BaseConfirm.vue';
import OfficeBankFormModal from './office-bank-form-modal.vue';
import OfficeSelectSearch from './office-select-search.vue';
import BaseBadge from '../base/BaseBadge.vue';

export default {
  mixins: [requestMixin],
  components: {
    BaseCard,
    BaseCheckbox,
    BaseConfirm,
    OfficeBankFormModal,
    OfficeSelectSearch,
    BaseBadge,
  },
  data() {
    return {
      officeSearch: null,
      loadingData: false,
      loadingDelete: false,
      loadingVerify: false,
      officeBanks: {
        data: [],
      },
      deleteConfirm: {
        visible: false,
        id: null,
      },
      verifyConfirm: {
        visible: false,
        phone: null,
        uuid: null,
      },
      formModal: {
        visible: false,
        data: {
          id: null,
          bankId: null,
          accountName: null,
          accountNumber: null,
          noAccountGl: null,
          costCenter: null,
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
      checkHasPermission: 'auth/checkHasPermission',
    }),
    loading() {
      return this.loadingData || this.loadingDelete || this.loadingVerify;
    },
  },
  methods: {
    checkQrisVerified(bank) {
      return (
        this.me.office_type === 'pusat' ||
        bank.attributes.office_bank_type !== 'qris' ||
        bank.attributes.verified
      );
    },
    async loadOfficeBanks() {
      this.loadingData = true;

      const [res, err] = await this.request(
        `/api/v1/offices/${
          this.officeSearch?.attributes
            ? this.officeSearch.attributes.office_id
            : this.me.office_id
        }/office-banks`,
        {
          params: {
            include: 'bank',
          },
        }
      );

      if (!err) {
        this.officeBanks = res;
      }

      this.loadingData = false;
    },
    async onChangeActive(bank, checked) {
      this.loadingData = true;

      await this.request(`/api/v1/office-banks/${bank.id}`, {
        method: 'patch',
        data: {
          data: {
            id: bank.id,
            type: 'office-banks',
            attributes: {
              is_active: checked,
            },
          },
        },
      });

      this.loadingData = false;
    },
    onCreate() {
      this.formModal.data.id = null;
      this.formModal.data.type = null;
      this.formModal.data.bank = null;
      this.formModal.data.accountName = null;
      this.formModal.data.accountNumber = null;
      this.formModal.data.noAccountGl = null;
      this.formModal.data.costCenter = null;
      this.formModal.data.qris = null;

      this.formModal.visible = true;
    },
    onEdit(bank) {
      const bankIncluded = this.getSingleIncluded(
        this.officeBanks,
        bank.relationships.bank.data.id
      );

      this.formModal.data.id = bank.id;
      this.formModal.data.bank = {
        id: bankIncluded.id,
        attributes: {
          name: bankIncluded.attributes.name,
        },
      };
      this.formModal.data.type = bank.attributes.office_bank_type;
      this.formModal.data.accountName = bank.attributes.account_name;
      this.formModal.data.accountNumber = bank.attributes.account_number;
      this.formModal.data.noAccountGl = bank.attributes.no_account_gl;
      this.formModal.data.costCenter = bank.attributes.cost_center;
      this.formModal.data.qris = bank.attributes.qris;

      this.formModal.visible = true;
    },
    onDelete(id) {
      this.deleteConfirm.id = id;
      this.deleteConfirm.visible = true;
    },
    async onRequestVerify(bank) {
      this.loadingVerify = true;

      const [res, err] = await this.request(
        `/api/v1/office-banks/${bank.id}/-actions/request-verify`
      );

      if (!err) {
        this.verifyConfirm.phone = res.phone;
        this.verifyConfirm.uuid = res.uuid;
        this.verifyConfirm.visible = true;
      }

      this.loadingVerify = false;
    },
    async onConfirmDelete() {
      this.loadingDelete = true;

      await this.request(`/api/v1/office-banks/${this.deleteConfirm.id}`, {
        method: 'delete',
      });

      this.deleteConfirm.visible = false;

      this.loadOfficeBanks();

      this.loadingDelete = false;
    },
    async onSaved(bank) {
      this.formModal.visible = false;

      this.loadOfficeBanks();

      if (!this.checkQrisVerified(bank.data)) {
        this.onRequestVerify(bank.data);
      }
    },
    onConfirmVerify() {
      const text = encodeURIComponent(
        `Verifikasi QRIS ${this.verifyConfirm.uuid}`
      );
      const url = `https://wa.me/${this.verifyConfirm.phone}?text=${text}`;

      const a = document.createElement('a');

      a.href = url;
      a.target = '_blank';

      a.click();
    },
  },
  created() {
    this.loadOfficeBanks();
  },
};
</script>
